// @flow
// @jsx h

import "abortcontroller-polyfill/dist/polyfill-patch-fetch";

import debounce from "lodash/debounce";

import {h} from "../_common/jsx-dom.svg.js";

import "./search-bar.scss";
import {submitV2AnalyticsEvent} from "./search-bar-analytics";

const TAG = "search-bar";

class SearchBar extends HTMLElement {
    eleSearchInput;
    eleSuggestedKeywordsDiv;
    eleSuggestedKeywordsList;
    eleSuggestedBrandsDiv;
    eleSuggestedBrandsList;
    eleSuggestedCategoriesDiv;
    eleSuggestedCategoriesList;
    eleSuggestedProductsDiv;
    eleSuggestedProductsList;
    eleSearchResultWrapper;
    dismissSearchResultTimeout;
    _onType;
    _fetchAbort;
    _userInput = "";
    constructor() {
        super();
        const debounceTime = this.getAttribute("debounce");
        this._onType = debounce(value => {
            this._doSuggest(value);
        }, debounceTime || 300);
        this._fetchAbort = new window.AbortController();
    }
    connectedCallback() {
        this.classList.add("header-main__search-wrapper");
        this.appendChild(
            <form class="header-main__search" onSubmit={this._doSearch}>
                <input
                    type="text"
                    id="search-input"
                    class="header-main__search-input"
                    placeholder="Search for products..."
                    ref={r => (this.eleSearchInput = r)}
                    autoComplete="off"
                    onKeyUp={this._checkForSuggest}
                    onKeyDown={(event) => {this._handleMaxUserSearchAnalytics(event)}}
                />
                <svg
                    id="header-main-search-clear"
                    class="header-main__search-clear"
                    onClick={this._clearSearch}
                    viewBox="0 0 50 50"
                >
                    <g>
                        <path d="M 9.15625 6.3125 L 6.3125 9.15625 L 22.15625 25 L 6.21875 40.96875 L 9.03125 43.78125 L 25 27.84375 L 40.9375 43.78125 L 43.78125 40.9375 L 27.84375 25 L 43.6875 9.15625 L 40.84375 6.3125 L 25 22.15625 Z " />
                    </g>
                </svg>
                <button class="header-main__search-button" type="submit" onClick={() => {this._handleMaxAnalytics("Search bar", null, this._isSearchResultDisplayed())}}>
                    Search
                </button>
            </form>
        );
        this.appendChild(
            <div
                id="search-results"
                class="search-result-wrapper"
                ref={r => (this.eleSearchResultWrapper = r)}
            >
                <div
                    class="search-result__section"
                    ref={r => (this.eleSuggestedKeywordsDiv = r)}
                >
                    <h4 class="search-result__section-title">
                        Suggested search
                    </h4>
                    <ul
                        class="search-result__list--text"
                        ref={r => (this.eleSuggestedKeywordsList = r)}
                    />
                </div>
                <div
                    class="search-result__section"
                    ref={r => (this.eleSuggestedBrandsDiv = r)}
                >
                    <h4 class="search-result__section-title">Brands</h4>
                    <ul
                        class="search-result__list--text"
                        ref={r => (this.eleSuggestedBrandsList = r)}
                    />
                </div>
                <div
                    class="search-result__section"
                    ref={r => (this.eleSuggestedCategoriesDiv = r)}
                >
                    <h4 class="search-result__section-title">Categories</h4>
                    <ul
                        class="search-result__list--text"
                        ref={r => (this.eleSuggestedCategoriesList = r)}
                    />
                </div>
                <div
                    class="search-result__section"
                    ref={r => (this.eleSuggestedProductsDiv = r)}
                >
                    <h4 class="search-result__section-title">
                        Related Products
                    </h4>
                    <ul
                        class="search-result__list--block"
                        ref={r => (this.eleSuggestedProductsList = r)}
                    />
                </div>
                <form onSubmit={this._doSearch}>
                    <button
                        class="header-main__search-button--results"
                        type="submit"
                        onClick={() => this._handleMaxAnalytics("Show all products", null, this._isSearchResultDisplayed())}
                    >
                        Show All Results
                    </button>
                </form>
            </div>
        );
        this._setupToggleSearchResult();
        this._populateSearchTerm();
    }

    _isSearchResultDisplayed = () => {
        return this.eleSearchResultWrapper.style.display !== "none" ? "Yes" : "No";
    }

    _storeUserInput = () => {
        this._userInput = this.eleSearchInput.value;
        sessionStorage.setItem("userInput", this._userInput);
    }

    _handleClearStorage = (storageItem: string) => {
        sessionStorage.removeItem(storageItem);
    }

    _handleRelatedProductsAnalytics = (method, value, displayed) => {
        this._storeUserInput();
        const analyticData = {
            searchBarMenuDisplayed: displayed,
            searchMethod: method,
            selectedKeyword: value,
            searchId: sessionStorage.getItem("searchId"),
            searchTerm: sessionStorage.getItem("userInput") || ""
        }
        submitV2AnalyticsEvent("viewRelatedProductDetails", analyticData);
        this._handleClearStorage("userInput");
        this._handleClearStorage("analyticsData");
    }

    _handleMaxUserSearchAnalytics = (event) => {
        this._userInput = event.target.value;
    }

    _handleMaxAnalytics = (method, value, displayed) => {
        this._storeUserInput();
        const analyticsData = {
            searchBarMenuDisplayed: displayed,
            searchMethod: method,
            selectedKeyword: value
        };
        sessionStorage.setItem('analyticsData', JSON.stringify(analyticsData));
    }

    _hideSearchResults = () =>
        (this.eleSearchResultWrapper.style.display = "none");

    _setupToggleSearchResult = () => {
        this.eleSearchInput.addEventListener("blur", () => {
            if (this.dismissSearchResultTimeout) {
                clearTimeout(this.dismissSearchResultTimeout);
            }
            this.dismissSearchResultTimeout = setTimeout(() => {
                this._hideSearchResults();
            }, 300);
        });
        this.eleSearchResultWrapper.addEventListener("focus", () => {
            if (this.dismissSearchResultTimeout) {
                clearTimeout(this.dismissSearchResultTimeout);
            }
        });
    };

    _checkForSuggest = e => {
        if (e.keyCode !== 13) {
            this._onType(e.target.value);
        }
    };

    _doSearch = e => {
        e.preventDefault();
        const value = this.eleSearchInput.value;
        if (value && value.trim()) {
            sessionStorage.setItem("userInput", this._userInput);
            const urlTemplate: string = ((this.getAttribute(
                "data-url"
            ): any): string);
            const url = urlTemplate.replace(
                "##search##",
                encodeURIComponent(value.trim())
            );
            window.location.href = url;
        }
    };
    _doSuggest = async value => {
        const trimmedValue = (value || "").trim();
        if (trimmedValue.length < this._getTriggerLength()) {
            this._clearResultList();
            this._hideSearchResults();
            return;
        }
        this._abortAnyExistingRequests();
        let result = {};
        try {
            result = await this._fetchSugguestResult(trimmedValue);
        } catch (err) {
            if (err.name === "AbortError") {
                return;
            }
        }
        this._displaySuggestResult(result);
    };
    _doGetSuggestedProductImages = async value => {
        const images = await this._fetchSuggestedProductsImages(value);
        this._displaySuggestedImages(images);
    };
    _fetchSugguestResult = async value => {
        const resp = await fetch(
            `/search/api/suggest?query=${encodeURIComponent(value)}`,
            {
                signal: this._fetchAbort.signal,
                credentials: "include"
            }
        );
        if (resp.ok) {
            return await resp.json();
        } else {
            throw resp.status;
        }
    };
    _abortAnyExistingRequests = () => {
        this._fetchAbort.abort();
        this._fetchAbort = new window.AbortController();
    };
    _getTriggerLength = () => {
        const length = parseInt(this.getAttribute("trigger-length"));
        return isNaN(length) || length <= 0 ? 3 : length;
    };

    _clearSearch = () => {
        this.eleSearchInput.value = "";
    };

    _clearResultList = () => {
        this.eleSuggestedKeywordsDiv.style.display = "none";
        this.eleSuggestedKeywordsList.innerHTML = "";
        this.eleSuggestedBrandsDiv.style.display = "none";
        this.eleSuggestedBrandsList.innerHTML = "";
        this.eleSuggestedProductsDiv.style.display = "none";
        this.eleSuggestedProductsList.innerHTML = "";
        this.eleSuggestedCategoriesDiv.style.display = "none";
        this.eleSuggestedCategoriesList.innerHTML = "";
    };

    _showAndMapResults = result => (key, eleDiv, eleList, mappingFunction) => {
        if (result[key] && result[key].length > 0)
            eleDiv.style.display = "block";
        else eleDiv.style.display = "none";

        (result[key] || [])
            .map(mappingFunction)
            .forEach(e => eleList.appendChild(e));
    };

    _displaySuggestResult = result => {
        if (result) {
            this._clearResultList();
            const mapper = this._showAndMapResults(result);

            mapper(
                `suggestedKeywords`,
                this.eleSuggestedKeywordsDiv,
                this.eleSuggestedKeywordsList,
                s => (
                    <li class="search-result__list-item--text">
                        <a href={s.url} onClick={() => {this._handleMaxAnalytics("Suggested search", s.keyword, this._isSearchResultDisplayed())}}>{s.keyword}</a>
                    </li>
                )
            );

            mapper(
                `suggestedBrands`,
                this.eleSuggestedBrandsDiv,
                this.eleSuggestedBrandsList,
                s => (
                    <li class="search-result__list-item--text">
                        <a href={s.url} onClick={() => {this._handleMaxAnalytics("Brands", s.title, this._isSearchResultDisplayed())}}>{s.title}</a>
                    </li>
                )
            );

            mapper(
                `suggestedCategories`,
                this.eleSuggestedCategoriesDiv,
                this.eleSuggestedCategoriesList,
                c => (
                    <li class="search-result__list-item--text">
                        <a href={c.url} onClick={() => {this._handleMaxAnalytics("Categories", c.suggestedKeyword + " in " + c.title, this._isSearchResultDisplayed())}}>
                            {c.suggestedKeyword} in {c.title}
                        </a>
                    </li>
                )
            );

            mapper(
                `suggestedProducts`,
                this.eleSuggestedProductsDiv,
                this.eleSuggestedProductsList,
                r => (
                    <li class="search-result__list-item--block">
                        <a class="search-result__list-item-img" href={r.url} onClick={() => {this._handleRelatedProductsAnalytics("Related products", r.title, this._isSearchResultDisplayed())}}>
                            <img
                                src="/product/static/images/w280-image-unavailable.jpg"
                                alt={r.title}
                                data-search-image-product-code={r.productCode}
                            />
                        </a>
                        <div class="search-result__list-item-name">
                            {r.title}
                        </div>
                    </li>
                )
            );

            this.eleSearchResultWrapper.style.display = "block";

            result.suggestedProducts
                ? this._doGetSuggestedProductImages(result.suggestedProducts)
                : null;
        }
    };
    _displaySuggestedImages = result => {
        if (result) {
            Object.keys(result.images).map(productCode => {
                //  Assume first image should be displayed
                if (result.images[productCode][0].resolutions.w280.url) {
                    this._setSuggestedProductImage(
                        productCode,
                        result.images[productCode][0].resolutions.w280.url
                    );
                }
            });
        }
    };
    _fetchSuggestedProductsImages = async value => {
        const headers = new Headers({
            "Content-Type": `application/json;charset=UTF-8`
        });
        const body = JSON.stringify(
            value.map(n => parseInt(n.productCode, 10))
        );
        const resp = await fetch(`/trsonline/products/images/batch`, {
            method: `POST`,
            credentials: `include`,
            headers,
            body
        });
        if (resp.ok) {
            return await resp.json();
        } else {
            throw resp.status;
        }
    };
    _setSuggestedProductImage = (productCode, imageURL) => {
        const elem = this.eleSuggestedProductsList.querySelector(
            "[data-search-image-product-code='" + productCode + "']"
        );
        if (elem) {
            elem.setAttribute("src", imageURL);
        }
    };
    _populateSearchTerm = () => {
        const location = /(^\/search\/)/.test(window.location.pathname);
        const queryParams = window.location.search.match(/[?&]query=([^&#]*)/);
        if (
            location &&
            queryParams &&
            queryParams.length > 0 &&
            queryParams[1]
        ) {
            this.eleSearchInput.value = decodeURIComponent(queryParams[1]);
        }
    };
}

customElements.define(TAG, SearchBar);


// WEBPACK FOOTER //
// ./javascript/search-bar/search-bar.js